import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Lock, Vault, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useBusiness } from "../context/BusinessContext";
import { CategoryIcon, ICONS } from "../components/CategoryIcon";
import { PageHeader, card, btnPrimary } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

function CategoryDialog({ open, onClose, initial, type, onSave }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("tag");
  useEffect(() => {
    setName(initial?.name || "");
    setIcon(initial?.icon || "tag");
  }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Kategoriyi Düzenle" : type === "income" ? "Yeni Girdi Kategorisi" : "Yeni Çıktı Kategorisi"}</DialogTitle></DialogHeader>
        <Input data-testid="category-name-input" placeholder="Kategori adı" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" />
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">İkon seçin</div>
          <div className="grid max-h-56 grid-cols-7 gap-2 overflow-y-auto">
            {Object.entries(ICONS).map(([k, I]) => (
              <button type="button" key={k} data-testid={`icon-option-${k}`} onClick={() => setIcon(k)}
                className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-colors ${icon === k ? "border-cyan-600 bg-cyan-50 text-cyan-700 ring-2 ring-cyan-500/30" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                <I className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>
        <button data-testid="category-save-button" className={btnPrimary} onClick={() => name.trim() && onSave({ name, icon, type: initial?.type || type })}>Kaydet</button>
      </DialogContent>
    </Dialog>
  );
}

function CategoryColumn({ title, items, type, onAdd, onEdit, onDelete }) {
  return (
    <div className={card} data-testid={`${type}-categories`}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
        <button data-testid={`add-${type}-category-button`} onClick={onAdd} className={btnPrimary}><Plus className="h-4 w-4" /> Ekle</button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((c) => (
          <div key={c.id} data-testid={`category-item-${c.id}`} className={`group flex items-center gap-3 rounded-xl border p-3 ${c.is_opening ? "border-sky-200 bg-sky-50" : "border-slate-200"}`}>
            <CategoryIcon name={c.icon} type={c.type} opening={c.is_opening} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-800">{c.name}</div>
              {c.is_opening && <div className="flex items-center gap-1 text-[11px] text-sky-700"><Lock className="h-3 w-3" /> Aylık ciroya eklenmez</div>}
            </div>
            <button data-testid={`edit-category-${c.id}`} onClick={() => onEdit(c)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
            {!c.is_opening && (
              <button data-testid={`delete-category-${c.id}`} onClick={() => onDelete(c)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegisterItem({ r, onRename, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(r.name);
  return (
    <div data-testid={`register-item-${r.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100 text-cyan-800"><Vault className="h-5 w-5" /></span>
      {edit ? (
        <Input data-testid={`register-edit-input-${r.id}`} value={name} onChange={(e) => setName(e.target.value)} className="h-9 flex-1 rounded-lg" />
      ) : (
        <div className="flex-1 truncate text-sm font-semibold text-slate-800">{r.name}</div>
      )}
      {edit ? (
        <button data-testid={`register-save-${r.id}`} onClick={() => { onRename(r, name); setEdit(false); }} className="rounded-lg bg-emerald-600 p-1.5 text-white"><Check className="h-4 w-4" /></button>
      ) : (
        <button data-testid={`edit-register-${r.id}`} onClick={() => setEdit(true)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
      )}
      <button data-testid={`delete-register-${r.id}`} onClick={() => onDelete(r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function Registers({ bid }) {
  const [regs, setRegs] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(async () => setRegs((await api.get(`/businesses/${bid}/registers`)).data), [bid]);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const add = (e) => {
    e.preventDefault();
    if (name.trim()) run(() => api.post(`/businesses/${bid}/registers`, { name }).then(() => setName("")), "Kasa eklendi");
  };
  return (
    <div className={card} data-testid="registers-section">
      <h3 className="font-heading text-lg font-bold text-slate-900">Kasalar</h3>
      <p className="mb-4 text-xs text-slate-500">Gün sonu kasa sayımında her kasa ayrı sayılır, toplamı beklenen kasa ile karşılaştırılır.</p>
      <form onSubmit={add} className="mb-4 flex gap-2">
        <Input data-testid="new-register-name-input" placeholder="Kasa adı (örn. POS, Kasa 2)" value={name} onChange={(e) => setName(e.target.value)} className="h-10 max-w-xs rounded-xl" />
        <button data-testid="add-register-button" className={btnPrimary}><Plus className="h-4 w-4" /> Ekle</button>
      </form>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {regs.map((r) => (
          <RegisterItem key={r.id} r={r}
            onRename={(x, n) => run(() => api.put(`/businesses/${bid}/registers/${x.id}`, { name: n }), "Kasa güncellendi")}
            onDelete={(x) => window.confirm(`"${x.name}" silinsin mi?`) && run(() => api.delete(`/businesses/${bid}/registers/${x.id}`), "Kasa silindi")} />
        ))}
      </div>
    </div>
  );
}

export default function Categories() {
  const { current } = useBusiness();
  const [cats, setCats] = useState([]);
  const [dlg, setDlg] = useState({ open: false, type: "income", initial: null });

  const load = useCallback(async () => {
    if (current) setCats((await api.get(`/businesses/${current.id}/categories`)).data);
  }, [current]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (body) => {
    try {
      if (dlg.initial) await api.put(`/businesses/${current.id}/categories/${dlg.initial.id}`, body);
      else await api.post(`/businesses/${current.id}/categories`, body);
      toast.success("Kategori kaydedildi");
      setDlg({ ...dlg, open: false });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/businesses/${current.id}/categories/${c.id}`);
      toast.success("Kategori silindi");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const col = (type, title) => (
    <CategoryColumn title={title} type={type} items={cats.filter((c) => c.type === type)}
      onAdd={() => setDlg({ open: true, type, initial: null })} onEdit={(c) => setDlg({ open: true, type, initial: c })} onDelete={del} />
  );

  return (
    <div className="space-y-6" data-testid="categories-page">
      <PageHeader title="Kategoriler" subtitle="Girdi ve çıktılar için kategorilerinizi yönetin. Kasa Açılış kategorisi aylık ciroya dahil edilmez." />
      <div className="grid gap-6 xl:grid-cols-2">
        {col("income", "Girdi Kategorileri")}
        {col("expense", "Çıktı Kategorileri")}
      </div>
      {current && <Registers bid={current.id} />}
      <CategoryDialog {...dlg} onClose={() => setDlg({ ...dlg, open: false })} onSave={save} />
    </div>
  );
}
